import React, { useCallback, useEffect, useState } from 'react';
import { Layers, CheckCircle2, Percent } from 'lucide-react';
import { useApi } from '../../../lib/api';
import PartnersSubNav from './PartnersSubNav';
import DonutChart from '../components/DonutChart';
import { sliceColor } from '../components/tierColors';
import '../../../styles/Dashboard.css';
import '../../../styles/workspace.css';
import Pagination from '../../../components/shared/Pagination';
import ConfirmDeleteModal from '../../../components/workspace/ConfirmDeleteModal';
import { SearchableSelect } from '../../../components/workspace/filters/FilterDropdowns';

const PAGE_SIZE = 10;

// Used for both adding and editing. When `initial` is passed the modal is in
// edit mode, and cleared optional fields are sent as null so they get cleared.
function TierModal({ initial, onSave, onClose, creating, error }) {
  const isEdit = !!initial;
  const [name, setName] = useState(initial?.name || '');
  const [pct, setPct] = useState(initial ? String(initial.commission_percentage) : '');
  const [validityDays, setValidityDays] = useState(initial?.validity_days ? String(initial.validity_days) : '');
  const [targetClients, setTargetClients] = useState(() => {
    if (initial?.target_clients) return String(initial.target_clients);
    const m = initial?.target_description?.match(/\d+/);
    return m ? m[0] : '';
  });

  const submit = (e) => {
    e.preventDefault();
    if (!name.trim() || pct === '') return;
    const empty = isEdit ? null : undefined;
    onSave({
      name: name.trim(),
      commissionPercentage: Number(pct),
      validityDays: validityDays ? Number(validityDays) : empty,
      targetClients: targetClients ? Number(targetClients) : empty,
    });
  };

  return (
    <div className="modal-backdrop" onClick={() => !creating && onClose()}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h2>{isEdit ? 'Edit Tier' : 'Add Tier'}</h2>
          <button className="modal-close" onClick={onClose} disabled={creating}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="admin-banner error">{error}</div>}
          <form onSubmit={submit}>
            <div className="form-row">
              <label>Name <span style={{ color: 'var(--status-danger-fg)' }}>*</span></label>
              <input className="form-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} autoFocus />
            </div>
            <div className="form-row">
              <label>Commission % <span style={{ color: 'var(--status-danger-fg)' }}>*</span></label>
              <input className="form-input" type="number" min="0" max="100" step="0.01" value={pct} onChange={(e) => setPct(e.target.value)} />
            </div>
            <div className="form-row">
              <label>Validity (days)</label>
              <input className="form-input" type="number" min="1" max="3650" value={validityDays} onChange={(e) => setValidityDays(e.target.value)} placeholder="Optional — leave blank for no expiry" />
            </div>
            <div className="form-row">
              <label>Target (paying clients)</label>
              <input
                className="form-input"
                type="text"
                inputMode="numeric"
                value={targetClients}
                onChange={(e) => setTargetClients(e.target.value.replace(/\D/g, '').replace(/^0+/, '').slice(0, 6))}
                placeholder="Optional — e.g. 5"
              />
              <small className="muted" style={{ fontSize: 12 }}>
                Partners reaching this many paying clients are flagged for an upgrade. Admin approval is always required.
              </small>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button type="button" className="btn-secondary" onClick={onClose} disabled={creating}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={creating || !name.trim() || pct === ''}>
                {creating ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? 'Save Changes' : 'Add Tier')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function PartnerTiersPage() {
  const api = useApi();
  const [items, setItems] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editTier, setEditTier] = useState(null);
  const [deactivateTier, setDeactivateTier] = useState(null);
  const [addError, setAddError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [tRes, pRes] = await Promise.all([api.adminListPartnerTiers(), api.adminListPartners()]);
      setItems(tRes.items || []);
      setPartners(pRes.items || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleCreate = async (payload) => {
    setCreating(true);
    setAddError(null);
    try {
      await api.adminCreatePartnerTier(payload);
      setAddOpen(false);
      refresh();
    } catch (err) {
      setAddError(err.response?.data?.error || err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleEdit = async (payload) => {
    setCreating(true);
    setAddError(null);
    try {
      await api.adminUpdatePartnerTier(editTier.id, payload);
      setEditTier(null);
      refresh();
    } catch (err) {
      setAddError(err.response?.data?.error || err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (tier) => {
    try {
      await api.adminUpdatePartnerTier(tier.id, { isActive: !tier.is_active });
      refresh();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  return (
    <div>
      <div className="admin-main-header">
        <div>
          <h1 className="admin-page-title">Partner Tiers</h1>
          <p className="admin-page-subtitle">Commission tier catalog and validity periods.</p>
        </div>
      </div>
      <PartnersSubNav />

      {items.length > 0 && (() => {
        const activeCount = items.filter((t) => t.is_active).length;
        const avgPct = items.reduce((s, t) => s + Number(t.commission_percentage), 0) / items.length;
        const tierSlices = items.map((t, i) => ({
          label: t.name,
          value: partners.filter((p) => p.tier_id === t.id).length,
          color: sliceColor(i),
        }));
        const tierTotal = tierSlices.reduce((s, sl) => s + sl.value, 0);

        return (
          <>
            <div className="admin-kpi-row">
              <div className="admin-kpi">
                <div className="admin-kpi-icon blue"><Layers size={18} /></div>
                <p className="admin-kpi-label">Total Tiers</p>
                <p className="admin-kpi-value">{items.length}</p>
              </div>
              <div className="admin-kpi ok">
                <div className="admin-kpi-icon green"><CheckCircle2 size={18} /></div>
                <p className="admin-kpi-label">Active Tiers</p>
                <p className="admin-kpi-value">{activeCount}</p>
              </div>
              <div className="admin-kpi">
                <div className="admin-kpi-icon purple"><Percent size={18} /></div>
                <p className="admin-kpi-label">Avg Commission</p>
                <p className="admin-kpi-value">{avgPct.toFixed(1)}%</p>
              </div>
            </div>

            {tierTotal > 0 && (
              <div className="admin-card">
                <h2 style={{ marginBottom: 16 }}>Partners per tier</h2>
                <div className="admin-donut-wrap">
                  <DonutChart slices={tierSlices} size={110} />
                  <div className="admin-donut-legend">
                    {tierSlices.map((sl) => {
                      const pct = Math.round((sl.value / tierTotal) * 100);
                      return (
                        <div key={sl.label} className="admin-donut-legend-item">
                          <div className="admin-donut-legend-label">
                            <div className="admin-donut-dot" style={{ background: sl.color }} />
                            {sl.label}
                          </div>
                          <div>
                            <span className="admin-donut-value">{sl.value}</span>
                            <span className="admin-donut-pct">({pct}%)</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </>
        );
      })()}

      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div className="admin-card-toolbar" style={{ margin: 0 }}>
          <SearchableSelect
            value={statusFilter}
            onChange={(v) => { setStatusFilter(v); setPage(1); }}
            options={[
              { value: '', label: 'All tiers' },
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ]}
            showAll={false}
            btnClass="form-select-btn"
            wrapStyle={{ width: 220 }}
            matchTriggerWidth
          />
        </div>
        <button className="btn-primary" onClick={() => setAddOpen(true)}>+ Add Tier</button>
      </div>

      {error && <div className="admin-banner error">{error}</div>}

      {loading ? (
        <p className="muted">Loading…</p>
      ) : items.length === 0 ? (
        <p className="muted">No tiers yet.</p>
      ) : (() => {
        const filtered = items.filter((t) => {
          if (statusFilter === 'active') return !!t.is_active;
          if (statusFilter === 'inactive') return !t.is_active;
          return true;
        });
        const pagedItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
        return filtered.length === 0 ? (
          <p className="muted">No {statusFilter} tiers.</p>
        ) : (
          <>
            <div className="entity-table-wrap">
              <table className="entity-table" style={{ width: '100%', minWidth: 760 }}>
                <colgroup>
                  <col style={{ width: '20%' }} />
                  <col style={{ width: 130 }} />
                  <col style={{ width: 110 }} />
                  <col />
                  <col style={{ width: 80 }} />
                  <col style={{ width: 170 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Commission %</th>
                    <th>Validity</th>
                    <th>Target</th>
                    <th>Active</th>
                    <th className="row-actions">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedItems.map((t) => (
                    <tr key={t.id}>
                      <td style={{ overflowWrap: 'anywhere' }}>{t.name}</td>
                      <td>{t.commission_percentage}%</td>
                      <td>{t.validity_days ? `${t.validity_days} days` : '—'}</td>
                      <td style={{ overflowWrap: 'anywhere' }} title={t.target_description || undefined}>
                        {t.target_clients
                          ? `Reach ${t.target_clients} paying client${t.target_clients === 1 ? '' : 's'}`
                          : t.target_description
                            ? <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{t.target_description}</span>
                            : '—'}
                      </td>
                      <td>{t.is_active ? 'Yes' : 'No'}</td>
                      <td className="row-actions">
                        <button className="btn-link-sm" onClick={() => { setAddError(null); setEditTier(t); }} style={{ marginRight: 12 }}>
                          Edit
                        </button>
                        <button className="btn-link-sm" onClick={() => (t.is_active ? setDeactivateTier(t) : handleToggleActive(t))}>
                          {t.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalItems={filtered.length} pageSize={PAGE_SIZE} onPageChange={setPage} />
          </>
        );
      })()}

      {(() => {
        const onTier = deactivateTier ? partners.filter((p) => p.tier_id === deactivateTier.id).length : 0;
        return (
          <ConfirmDeleteModal
            open={!!deactivateTier}
            title={deactivateTier ? `Deactivate ${deactivateTier.name}?` : ''}
            message={onTier > 0
              ? `${onTier} partner${onTier === 1 ? ' is' : 's are'} on this tier. They will stop earning commission on new client payments until you move them to an active tier. Commission already earned is not affected.`
              : 'No partners are on this tier. It will no longer be available to assign.'}
            confirmLabel="Deactivate"
            onConfirm={async () => { await api.adminUpdatePartnerTier(deactivateTier.id, { isActive: false }); refresh(); }}
            onClose={() => setDeactivateTier(null)}
          />
        );
      })()}

      {editTier && (
        <TierModal
          initial={editTier}
          onSave={handleEdit}
          onClose={() => { setEditTier(null); setAddError(null); }}
          creating={creating}
          error={addError}
        />
      )}

      {addOpen && (
        <TierModal
          onSave={handleCreate}
          onClose={() => { setAddOpen(false); setAddError(null); }}
          creating={creating}
          error={addError}
        />
      )}
    </div>
  );
}
